// Deposits — customer payments / check registry (Priority Pest Defense).
//
// Money IN only. Subcontractor pay, employee pay, monthly bills and bank
// deductions are on the Expenses page (Expenses.js). Same collections and
// transaction mirroring as before.
//
// Writes: deposits (+ mirror row in transactions via txId), job_payment_totals,
// approvedInvoices paid status. Payments entered on Approved Invoices (Paid
// button), Job Estimate Spreadsheet and Bank Statement Entries also land in
// "deposits" and show up here.
//
// Pest-control specifics:
//   * A recurring contract is ONE job, so every visit's invoice has the same
//     WO. A payment can name the invoice / visit it pays (approvedInvoiceId +
//     canonical occurrenceKey). Payments that don't are applied to that WO's
//     invoices oldest visit first (utils/depositsInvoiceMatching.js), so one
//     month's check never marks every month paid.
//   * Jobs without a WO are keyed by job id — the same value Approved
//     Invoices puts in a payment's invoiceNumber.

import React, { useState, useEffect, useMemo } from "react";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
// Kept: other pages rely on the global .excel-table / .summary-card rules this
// file has always loaded.
import "./Deposits.css";
import "./FinancePages.css";
import * as XLSX from "xlsx";
import DepositsFinModal from "../components/depositsFinModal";
import {
  BULK_TEMPLATES,
  todayStr,
  parseAmount,
  formatMoney,
  formatDateMDY,
  sortByDateDesc,
  upsertTransaction,
  deleteLinkedTransaction,
  buildTxFromDeposit,
  buildTxFromSubPay,
  buildTxFromEmpPay,
  buildTxFromBill,
  buildTxFromBankDed,
  depositHasOwnLedgerRow,
} from "../utils/financeEntries";
import {
  findDuplicateGroups,
  findMatchesForNew,
  depositSourceLabel as baseSourceLabel,
  isBankStatementDeposit,
} from "../utils/depositDuplicates";
import {
  allocateDepositsToInvoices,
  invoicePaymentKey,
  invoiceTotal,
  invoiceStatus,
  invoiceVisitKey,
  invoiceBalance,
  isInvoiceCovered,
  isVoidInvoice,
  withVisitInWO,
} from "../utils/depositsInvoiceMatching";
import {
  isRecurring,
  toDateKey,
  getCanonicalKeyForScheduledKey,
  getCompletedDateSet,
} from "../utils/recurringOccurrenceUtils";

const paymentTypeOptions = ["Deposit", "Progress", "Final", "Service Payment", "Other"];

const EMPTY_CHECK = {
  property: "",
  amount: "",
  checkNumber: "",
  paymentType: "Deposit",
  invoiceNumber: "",
  invoiceNumber2: "",
  approvedInvoiceId: "",
  occurrenceKey: "",
  description: "",
  company: "",
  date: todayStr(),
};

/** Excel dates arrive as serial numbers or M/D/YYYY text; store YYYY-MM-DD. */
const normalizeSheetDate = (value) => {
  if (value === undefined || value === null || value === "") return todayStr();
  if (typeof value === "number") {
    const d = XLSX.SSF.parse_date_code(value);
    if (d) return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const year = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${year}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  }
  return s;
};

const depositSourceLabel = (d) => {
  if (String(d?.createdFrom || "").startsWith("ApprovedInvoices")) return "Approved Invoices";
  return baseSourceLabel(d);
};

const sourceTagClass = (d) =>
  isBankStatementDeposit(d)
    ? "bank"
    : String(d?.createdFrom || "").startsWith("JobEstimate") || String(d?.createdFrom || "").startsWith("ApprovedInvoices")
    ? "sheet"
    : "manual";

/** Key a payment uses for a job: its WO, or its id when it has no WO. */
const jobPaymentKey = (job = {}) => String(job.wo !== undefined && job.wo !== null && job.wo !== "" ? job.wo : job.id || "").trim();
const jobSite = (job = {}) => job.siteName || job.propertyName || "";
const invoiceSite = (inv = {}) => inv.siteName || inv.propertyName || "";

// Duplicate check with the visit folded into the WO (see withVisitInWO).
const findDupGroupsForPriority = (deposits) => {
  const byId = new Map(deposits.map((d) => [d.id, d]));
  return findDuplicateGroups(deposits.map(withVisitInWO)).map((g) => ({
    ...g,
    items: g.items.map((i) => byId.get(i.id) || i),
  }));
};
const findMatchesForPriority = (candidate, deposits) => {
  const byId = new Map(deposits.map((d) => [d.id, d]));
  return findMatchesForNew(withVisitInWO(candidate), deposits.map(withVisitInWO)).map((d) => byId.get(d.id) || d);
};

const Deposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [jobsMap, setJobsMap] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add / edit
  const [formMode, setFormMode] = useState(null); // null | "add" | "edit"
  const [form, setForm] = useState(EMPTY_CHECK);
  const [isSaving, setIsSaving] = useState(false);
  const [dupConfirm, setDupConfirm] = useState(null); // { data, matches }

  // Duplicate review
  const [showReview, setShowReview] = useState(false);
  const [reviewFocus, setReviewFocus] = useState(null);
  const [keepChoice, setKeepChoice] = useState({}); // groupKey -> { id: true(keep) | false(delete) }
  const [reviewBusy, setReviewBusy] = useState("");

  // Tools
  const [showTools, setShowTools] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState("");
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [txBackfillStatus, setTxBackfillStatus] = useState("");
  const [isTxBackfilling, setIsTxBackfilling] = useState(false);

  // Filters
  const [filterProperty, setFilterProperty] = useState("");
  const [searchText, setSearchText] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [expandedSplitGroups, setExpandedSplitGroups] = useState({});

  // Bulk upload
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  /* ------------------------------------------------------------------ */
  /* Load                                                               */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        const [depSnap, propSnap, compSnap, jobsSnap, invSnap] = await Promise.all([
          getDocs(collection(db, "deposits")),
          getDocs(collection(db, "properties")),
          getDocs(collection(db, "companies")),
          getDocs(collection(db, "jobs")),
          getDocs(collection(db, "approvedInvoices")),
        ]);

        const jobsLookup = {};
        jobsSnap.docs.forEach((d) => {
          const data = { id: d.id, ...d.data() };
          const key = jobPaymentKey(data);
          if (key && !jobsLookup[key]) jobsLookup[key] = data;
        });
        setJobsMap(jobsLookup);

        setDeposits(sortByDateDesc(depSnap.docs.map((d) => ({ id: d.id, ...d.data() }))));
        setInvoices(invSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setProperties(
          propSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.propertyName || "").localeCompare(b.propertyName || ""))
        );
        setCompanies(
          compSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.companyName || "").localeCompare(b.companyName || ""))
        );
      } catch (err) {
        console.error("Error loading deposits:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, []);

  /* ------------------------------------------------------------------ */
  /* Sync helpers                                                       */
  /* ------------------------------------------------------------------ */

  // Live paid amount per invoice, from what's on screen.
  const paidByInvoiceId = useMemo(() => allocateDepositsToInvoices(invoices, deposits), [invoices, deposits]);

  /**
   * Re-read deposits + invoices and set Paid / not Paid on every invoice that
   * shares this WO. Same fields Approved Invoices writes.
   */
  const syncApprovedInvoicePaidStatus = async (invoiceNumber) => {
    const invoiceKey = String(invoiceNumber || "").trim();
    if (!invoiceKey) return;

    const [depositsSnap, approvedInvoicesSnap] = await Promise.all([
      getDocs(collection(db, "deposits")),
      getDocs(collection(db, "approvedInvoices")),
    ]);
    const allDeposits = depositsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const allInvoices = approvedInvoicesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const paid = allocateDepositsToInvoices(allInvoices, allDeposits);
    const now = new Date().toISOString();
    const patched = {};

    for (const invoice of allInvoices) {
      if (invoicePaymentKey(invoice) !== invoiceKey || isVoidInvoice(invoice)) continue;
      if (!invoiceTotal(invoice)) continue;
      const totalPaid = paid[invoice.id] || 0;
      const status = invoiceStatus(invoice);

      if (isInvoiceCovered(invoice, totalPaid)) {
        if (status !== "Paid" || invoice.paid !== true || Math.round((invoice.paidAmount || 0) * 100) !== Math.round(totalPaid * 100)) {
          const patch = {
            status: "Paid",
            paid: true,
            paidAt: invoice.paidAt || now,
            paidAmount: totalPaid,
            paymentMatchedFrom: status === "Paid" && invoice.paymentMatchedFrom ? invoice.paymentMatchedFrom : "deposits",
            updatedAt: now,
          };
          await updateDoc(doc(db, "approvedInvoices", invoice.id), patch);
          patched[invoice.id] = patch;
        }
      } else if (status === "Paid" || invoice.paid === true) {
        // Only un-pay an invoice that was marked Paid automatically from
        // payments, or whose payments are now all gone. An invoice someone
        // marked Paid with the Paid button on Approved Invoices (e.g. a short
        // payment they accepted) stays Paid while its payment exists.
        const matchedFrom = String(invoice.paymentMatchedFrom || "");
        const autoMatched =
          matchedFrom === "deposits" ||
          matchedFrom === "ApprovedInvoicesLoad" ||
          matchedFrom === "JobEstimateSpreadsheet";
        if (!matchedFrom) continue;
        if (!autoMatched && totalPaid > 0) continue;
        const wasSent = invoice.sent === true || invoice.sentAt || invoice.emailedAt;
        const patch = {
          status: wasSent ? "Sent" : "Approved",
          paid: false,
          paidAmount: totalPaid,
          paymentMatchedFrom: "deposits",
          updatedAt: now,
        };
        await updateDoc(doc(db, "approvedInvoices", invoice.id), patch);
        patched[invoice.id] = patch;
      } else if (Math.round((invoice.paidAmount || 0) * 100) !== Math.round(totalPaid * 100)) {
        const patch = { paidAmount: totalPaid, updatedAt: now };
        await updateDoc(doc(db, "approvedInvoices", invoice.id), patch);
        patched[invoice.id] = patch;
      }
    }

    setInvoices(allInvoices.map((inv) => (patched[inv.id] ? { ...inv, ...patched[inv.id] } : inv)));
  };

  const syncJobTotalsFromCheck = async (wo) => {
    if (!wo) return;
    const jobKey = String(wo);
    const depSnap = await getDocs(collection(db, "deposits"));

    let total = 0;
    let lastDeposit = null;
    const paidByOccurrence = {};
    depSnap.docs.forEach((d) => {
      const data = d.data();
      if (String(data.invoiceNumber) === jobKey) {
        const amt = parseAmount(data.amount);
        total += amt;
        lastDeposit = data;
        const visit = toDateKey(data.occurrenceKey);
        if (visit) paidByOccurrence[visit] = Math.round(((paidByOccurrence[visit] || 0) + amt) * 100) / 100;
      }
    });

    const job = jobsMap[jobKey];
    await setDoc(
      doc(db, "job_payment_totals", jobKey),
      {
        wo: jobKey,
        jobId: job?.id || null,
        siteName: jobSite(job || {}),
        paidTotal: total,
        paidByOccurrence,
        status: job?.status || "Unknown",
        company: lastDeposit?.company || "",
        description: lastDeposit?.description || "",
        lastCheckNumber: lastDeposit?.checkNumber || "",
        invoiceNumber2: lastDeposit?.invoiceNumber2 || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const resyncWO = async (wo) => {
    if (!wo) return;
    await syncJobTotalsFromCheck(wo);
    await syncApprovedInvoicePaidStatus(wo);
  };

  const resolveJobIdFromWO = (wo) => (wo ? jobsMap[String(wo)]?.id || null : null);

  const getWOsForProperty = (propertyName) => {
    if (!propertyName) return [];
    return Object.values(jobsMap)
      .filter((job) => jobSite(job) === propertyName)
      .sort((a, b) => jobPaymentKey(a).localeCompare(jobPaymentKey(b), undefined, { numeric: true }));
  };

  const suggestWOForRow = (property, date) => {
    const jobs = getWOsForProperty(property);
    const target = new Date(date);
    let best = null;
    for (const j of jobs) {
      if (!j.startDate) continue;
      const jd = new Date(j.startDate?.toDate ? j.startDate.toDate() : j.startDate);
      const bd = best ? new Date(best.startDate?.toDate ? best.startDate.toDate() : best.startDate) : null;
      if (!best || Math.abs(jd - target) < Math.abs(bd - target)) best = j;
    }
    return best ? jobPaymentKey(best) : "";
  };

  const formatShortDate = (dateVal) => {
    if (!dateVal) return "No start date";
    try {
      const d = typeof dateVal === "string" ? new Date(dateVal) : dateVal.toDate?.() || new Date(dateVal);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "Invalid date";
    }
  };

  /** Canonical visit key for an invoice, using its job's reschedule map. */
  const canonicalVisitForInvoice = (inv) => {
    const key = invoiceVisitKey(inv);
    if (!key) return "";
    const job = jobsMap[invoicePaymentKey(inv)];
    return (job && isRecurring(job) && getCanonicalKeyForScheduledKey(job, key)) || key;
  };

  const invoiceLabel = (inv) => {
    const paid = paidByInvoiceId[inv.id] || 0;
    const visit = invoiceVisitKey(inv);
    return `#${inv.invoiceNumber || invoicePaymentKey(inv)}${visit ? ` · visit ${formatDateMDY(visit)}` : ""} · ${formatMoney(
      invoiceTotal(inv)
    )} · ${isInvoiceCovered(inv, paid) ? "paid" : `owes ${formatMoney(invoiceBalance(inv, paid))}`}`;
  };

  /* ------------------------------------------------------------------ */
  /* Maintenance tools                                                  */
  /* ------------------------------------------------------------------ */

  const backfillJobTotalsFromExistingChecks = async () => {
    if (isBackfilling) return;
    try {
      setIsBackfilling(true);
      setBackfillStatus("Running…");
      const depSnap = await getDocs(collection(db, "deposits"));
      const uniqueWOs = new Set();
      depSnap.docs.forEach((d) => {
        const data = d.data();
        if (data.invoiceNumber) uniqueWOs.add(String(data.invoiceNumber));
      });
      for (const wo of uniqueWOs) await resyncWO(wo);
      setBackfillStatus(`Done — ${uniqueWOs.size} jobs updated`);
    } catch (err) {
      console.error(err);
      setBackfillStatus("Failed — check console");
    } finally {
      setIsBackfilling(false);
    }
  };

  const backfillTransactions = async () => {
    if (isTxBackfilling) return;
    try {
      setIsTxBackfilling(true);
      setTxBackfillStatus("Running…");
      let created = 0;

      const sources = [
        // Deposits from Bank Statement Entries already have their own ledger
        // row, so they are skipped here to avoid counting that money twice.
        { col: "deposits", build: (d) => buildTxFromDeposit(d, resolveJobIdFromWO(d.invoiceNumber)), skip: depositHasOwnLedgerRow },
        { col: "subcontractorPay", build: buildTxFromSubPay },
        { col: "employeePay", build: buildTxFromEmpPay },
        { col: "monthlyBills", build: buildTxFromBill },
        { col: "bankDeductions", build: buildTxFromBankDed },
      ];

      for (const src of sources) {
        const snap = await getDocs(collection(db, src.col));
        for (const d of snap.docs) {
          const data = { id: d.id, ...d.data() };
          if (data.txId || (src.skip && src.skip(data))) continue;
          if (src.col === "deposits" && (data.splitInvoiceOnly || data.invoiceOnly)) continue;
          const txId = await upsertTransaction({ txId: null, payload: src.build(data) });
          await updateDoc(doc(db, src.col, d.id), { txId });
          if (src.col === "deposits") {
            setDeposits((prev) => prev.map((x) => (x.id === d.id ? { ...x, txId } : x)));
          }
          created++;
        }
      }
      setTxBackfillStatus(`Done — ${created} entr${created === 1 ? "y" : "ies"} added to Financials`);
    } catch (err) {
      console.error(err);
      setTxBackfillStatus("Failed — check console");
    } finally {
      setIsTxBackfilling(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Add / edit / delete                                                */
  /* ------------------------------------------------------------------ */

  const openAdd = () => {
    setForm({ ...EMPTY_CHECK, date: todayStr() });
    setFormMode("add");
  };

  const openEdit = (entry) => {
    const clean = { ...EMPTY_CHECK, ...entry };
    Object.keys(clean).forEach((k) => k.startsWith("__") && delete clean[k]);
    setForm(clean);
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode(null);
    setForm(EMPTY_CHECK);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "property") {
      setForm((prev) => ({ ...prev, property: value, invoiceNumber: "", approvedInvoiceId: "", occurrenceKey: "" }));
      return;
    }
    if (name === "invoiceNumber") {
      setForm((prev) => ({ ...prev, invoiceNumber: value, approvedInvoiceId: "", occurrenceKey: "" }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // "Pays invoice / visit" select. Value is "inv:<id>" or "visit:<YYYY-MM-DD>".
  const handleVisitChoice = (value) => {
    if (!value) {
      setForm((prev) => ({ ...prev, approvedInvoiceId: "", occurrenceKey: "" }));
      return;
    }
    if (value.startsWith("visit:")) {
      setForm((prev) => ({ ...prev, approvedInvoiceId: "", occurrenceKey: value.slice(6) }));
      return;
    }
    const inv = invoices.find((i) => i.id === value.slice(4));
    if (!inv) return;
    applyInvoiceToForm(inv);
  };

  const applyInvoiceToForm = (inv) => {
    const paid = paidByInvoiceId[inv.id] || 0;
    const balance = invoiceBalance(inv, paid);
    setForm((prev) => ({
      ...prev,
      property: prev.property || invoiceSite(inv),
      invoiceNumber: invoicePaymentKey(inv),
      approvedInvoiceId: inv.id,
      occurrenceKey: canonicalVisitForInvoice(inv),
      amount: prev.amount === "" || prev.amount === undefined ? (balance || invoiceTotal(inv) || "") : prev.amount,
      description: prev.description || inv.description || inv.jobDescription || "",
    }));
  };

  const saveNewCheck = async (dataToSave) => {
    setIsSaving(true);
    try {
      const docRef = await addDoc(collection(db, "deposits"), dataToSave);
      const txPayload = buildTxFromDeposit({ id: docRef.id, ...dataToSave }, resolveJobIdFromWO(dataToSave.invoiceNumber));
      const txId = await upsertTransaction({ txId: null, payload: txPayload });
      await updateDoc(doc(db, "deposits", docRef.id), { txId });
      setDeposits((prev) => sortByDateDesc([...prev, { id: docRef.id, ...dataToSave, txId }]));
      await resyncWO(dataToSave.invoiceNumber);
      closeForm();
    } catch (err) {
      console.error(err);
      alert("Could not save this payment. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const withJobLink = (data) => {
    const job = data.invoiceNumber ? jobsMap[String(data.invoiceNumber)] : null;
    return { ...data, jobId: job?.id || data.jobId || "" };
  };

  const handleSubmitForm = async () => {
    if (!form.property || !form.amount || !form.date) {
      alert("Property, Amount, and Date are required.");
      return;
    }
    if (form.invoiceNumber && !jobsMap[String(form.invoiceNumber)]) {
      alert(`Work Order ${form.invoiceNumber} was not found. This payment will be saved but will not link to Job History.`);
    }

    if (formMode === "add") {
      const dataToSave = withJobLink({
        ...form,
        amount: parseAmount(form.amount),
        createdFrom: "Deposits",
        createdAt: new Date().toISOString(),
      });
      const matches = findMatchesForPriority(dataToSave, deposits);
      if (matches.length) {
        setDupConfirm({ data: dataToSave, matches });
        return;
      }
      await saveNewCheck(dataToSave);
      return;
    }

    // Edit
    setIsSaving(true);
    try {
      const oldInvoiceNumber = deposits.find((d) => d.id === form.id)?.invoiceNumber;
      const updated = withJobLink({ ...form, amount: parseAmount(form.amount), updatedAt: new Date().toISOString() });
      const { id, ...toWrite } = updated;
      await updateDoc(doc(db, "deposits", id), toWrite);

      // Bank statement deposits are counted through Bank Statement Entries.
      if (!depositHasOwnLedgerRow(updated)) {
        const txId = await upsertTransaction({
          txId: updated.txId || null,
          payload: buildTxFromDeposit(updated, resolveJobIdFromWO(updated.invoiceNumber)),
        });
        if (!updated.txId && txId) {
          await updateDoc(doc(db, "deposits", id), { txId });
          updated.txId = txId;
        }
      }

      setDeposits((prev) => sortByDateDesc(prev.map((d) => (d.id === id ? updated : d))));
      await resyncWO(updated.invoiceNumber);
      if (oldInvoiceNumber && String(oldInvoiceNumber) !== String(updated.invoiceNumber)) {
        await resyncWO(oldInvoiceNumber);
      }
      closeForm();
    } catch (err) {
      console.error(err);
      alert("Could not save changes. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  /** Delete one deposit + its ledger row. Does not resync; caller does. */
  const removeDeposit = async (entry) => {
    await deleteDoc(doc(db, "deposits", entry.id));
    if (!depositHasOwnLedgerRow(entry)) {
      await deleteLinkedTransaction({ txId: entry.txId, sourceCollection: "deposits", sourceId: entry.id });
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete this ${formatMoney(parseAmount(entry.amount))} payment from ${entry.property || "this property"}?`)) return;
    try {
      await removeDeposit(entry);
      setDeposits((prev) => prev.filter((d) => d.id !== entry.id));
      await resyncWO(entry.invoiceNumber);
    } catch (err) {
      console.error(err);
      alert("Could not delete this payment.");
    }
  };

  /* ------------------------------------------------------------------ */
  /* Duplicates                                                         */
  /* ------------------------------------------------------------------ */

  // Checked across ALL deposits, not just the filtered view, so a filter can
  // never hide a duplicate.
  const duplicateGroups = useMemo(() => findDupGroupsForPriority(deposits), [deposits]);

  const dupGroupById = useMemo(() => {
    const m = {};
    duplicateGroups.forEach((g) => g.items.forEach((d) => (m[d.id] = g)));
    return m;
  }, [duplicateGroups]);

  const duplicateExposure = duplicateGroups.reduce(
    (sum, g) => sum + parseAmount(g.items[0].amount) * (g.items.length - 1),
    0
  );

  const openReview = (groupKey = null) => {
    setReviewFocus(groupKey);
    setShowReview(true);
    setTimeout(() => {
      if (groupKey) document.getElementById(`dup-${groupKey}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 50);
  };

  // By default keep the recommended entry and delete the rest; each row can be
  // flipped, as long as at least one entry stays.
  const isKept = (group, id) => {
    const choice = keepChoice[group.key]?.[id];
    return choice === undefined ? id === group.keepId : choice;
  };
  const toggleKeep = (group, id) => {
    const next = !isKept(group, id);
    const keptCount = group.items.filter((d) => (d.id === id ? next : isKept(group, d.id))).length;
    if (keptCount === 0) return;
    setKeepChoice((p) => ({ ...p, [group.key]: { ...(p[group.key] || {}), [id]: next } }));
  };

  const deleteDuplicatesInGroup = async (group) => {
    const toDelete = group.items.filter((d) => !isKept(group, d.id));
    if (!toDelete.length) {
      alert("Every entry in this group is marked Keep. Use “Not a duplicate” instead.");
      return;
    }
    const kept = group.items.find((d) => isKept(group, d.id));
    const lines = toDelete.map(
      (d) => `• ${formatDateMDY(d.date)}  ${formatMoney(parseAmount(d.amount))}  check ${d.checkNumber || "—"}  (${depositSourceLabel(d)})`
    );
    if (
      !window.confirm(
        `Delete ${toDelete.length} duplicate${toDelete.length === 1 ? "" : "s"} and keep the ${depositSourceLabel(kept)} entry?\n\n${lines.join("\n")}`
      )
    )
      return;

    setReviewBusy(group.key);
    try {
      for (const d of toDelete) await removeDeposit(d);
      const ids = new Set(toDelete.map((d) => d.id));
      setDeposits((prev) => prev.filter((d) => !ids.has(d.id)));
      const wos = new Set(group.items.map((d) => d.invoiceNumber).filter(Boolean));
      for (const wo of wos) await resyncWO(wo);
    } catch (err) {
      console.error(err);
      alert("Something went wrong deleting duplicates. Refresh and check the list.");
    } finally {
      setReviewBusy("");
    }
  };

  const dismissGroup = async (group) => {
    setReviewBusy(group.key);
    try {
      await Promise.all(
        group.items.map((d) => updateDoc(doc(db, "deposits", d.id), { duplicateDismissedSig: group.signature }))
      );
      const ids = new Set(group.items.map((d) => d.id));
      setDeposits((prev) => prev.map((d) => (ids.has(d.id) ? { ...d, duplicateDismissedSig: group.signature } : d)));
    } catch (err) {
      console.error(err);
      alert("Could not save that. Please try again.");
    } finally {
      setReviewBusy("");
    }
  };

  /* ------------------------------------------------------------------ */
  /* Filtering, split grouping, duplicate grouping                      */
  /* ------------------------------------------------------------------ */

  const filteredDeposits = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return deposits
      .filter((d) => !filterProperty || d.property === filterProperty)
      .filter((d) => {
        if (!q) return true;
        return `${d.company || ""} ${d.description || ""} ${d.property || ""} ${d.checkNumber || ""} ${d.invoiceNumber || ""} ${d.invoiceNumber2 || ""}`
          .toLowerCase()
          .includes(q);
      })
      .filter((d) => {
        if (!filterStart && !filterEnd) return true;
        const dt = new Date(d.date);
        if (filterStart && dt < new Date(filterStart)) return false;
        if (filterEnd && dt > new Date(filterEnd)) return false;
        return true;
      })
      .sort((a, b) => {
        if (sortBy === "wo")
          return String(a.invoiceNumber || "").localeCompare(String(b.invoiceNumber || ""), undefined, { numeric: true, sensitivity: "base" });
        if (sortBy === "property") return (a.property || "").localeCompare(b.property || "");
        if (sortBy === "company") return (a.company || "").localeCompare(b.company || "");
        if (sortBy === "amount") return parseAmount(b.amount) - parseAmount(a.amount);
        return new Date(b.date || "1900-01-01") - new Date(a.date || "1900-01-01");
      });
  }, [deposits, filterProperty, searchText, filterStart, filterEnd, sortBy]);

  const displayRows = useMemo(() => {
    // 1) Collapse split checks into one summary row.
    const rows = [];
    const rendered = new Set();
    const grouped = filteredDeposits.reduce((map, entry) => {
      if (entry.splitCheck && entry.checkGroupId) (map[entry.checkGroupId] = map[entry.checkGroupId] || []).push(entry);
      return map;
    }, {});

    filteredDeposits.forEach((entry) => {
      const groupKey = entry.splitCheck && entry.checkGroupId ? entry.checkGroupId : "";
      if (!groupKey) {
        rows.push(entry);
        return;
      }
      if (rendered.has(groupKey)) return;
      rendered.add(groupKey);

      const items = grouped[groupKey] || [];
      const invoiceOnlyRow = items.find((i) => i.splitInvoiceOnly || i.invoiceOnly) || null;
      const summarySource = invoiceOnlyRow || items[0] || entry;
      const paidItems = items.filter((i) => !(i.splitInvoiceOnly || i.invoiceOnly));
      const groupTotal = paidItems.reduce((s, i) => s + parseAmount(i.amount), 0);

      rows.push({
        ...summarySource,
        id: `split-summary-${groupKey}`,
        __isSplitSummary: true,
        __splitGroupKey: groupKey,
        __splitCount: paidItems.length,
        __displayAmount: summarySource.originalCheckAmount || groupTotal,
        __linkedWOList: Array.from(new Set(paidItems.map((i) => String(i.invoiceNumber || "").trim()).filter(Boolean))),
        __invoiceOnlyWOList: Array.from(
          new Set(items.filter((i) => i.splitInvoiceOnly || i.invoiceOnly).map((i) => String(i.invoiceNumber || "").trim()).filter(Boolean))
        ),
        __hasDuplicateChild: paidItems.some((i) => dupGroupById[i.id]),
      });

      if (expandedSplitGroups[groupKey]) {
        paidItems.forEach((i) => rows.push({ ...i, __isSplitChild: true, __splitGroupKey: groupKey }));
      }
    });

    // 2) Pull possible duplicates next to each other so they're compared at a glance.
    const out = [];
    const placed = new Set();
    rows.forEach((row) => {
      if (placed.has(row.id)) return;
      const g = !row.__isSplitSummary && !row.__isSplitChild ? dupGroupById[row.id] : null;
      if (!g) {
        out.push(row);
        placed.add(row.id);
        return;
      }
      const members = rows.filter((r) => !r.__isSplitChild && !r.__isSplitSummary && dupGroupById[r.id] === g);
      members.forEach((m, i) => {
        out.push({ ...m, __dupFirst: i === 0, __dupLast: i === members.length - 1 });
        placed.add(m.id);
      });
    });
    return out;
  }, [filteredDeposits, expandedSplitGroups, dupGroupById]);

  /* ------------------------------------------------------------------ */
  /* Totals                                                             */
  /* ------------------------------------------------------------------ */

  const allTimeTotal = deposits.reduce((s, d) => s + parseAmount(d.amount), 0);
  const viewTotal = filteredDeposits.reduce((s, d) => s + parseAmount(d.amount), 0);
  const viewCount = filteredDeposits.filter((d) => !(d.splitInvoiceOnly || d.invoiceOnly)).length;

  const viewRange = useMemo(() => {
    if (filterStart || filterEnd) return `${filterStart ? formatDateMDY(filterStart) : "…"} – ${filterEnd ? formatDateMDY(filterEnd) : "…"}`;
    const dates = filteredDeposits.map((d) => d.date).filter(Boolean).sort();
    return dates.length ? `${formatDateMDY(dates[0])} – ${formatDateMDY(dates[dates.length - 1])}` : "—";
  }, [filteredDeposits, filterStart, filterEnd]);

  const companyTotals = useMemo(() => {
    const m = {};
    filteredDeposits.forEach((d) => {
      const key = d.company || "No company";
      m[key] = (m[key] || 0) + parseAmount(d.amount);
    });
    return Object.entries(m)
      .filter(([, v]) => v !== 0)
      .sort((a, b) => b[1] - a[1]);
  }, [filteredDeposits]);

  // Open (unpaid) invoices — what customers still owe.
  const openInvoiceSummary = useMemo(() => {
    let owed = 0;
    let count = 0;
    invoices.forEach((inv) => {
      if (isVoidInvoice(inv) || !invoiceTotal(inv)) return;
      const paid = paidByInvoiceId[inv.id] || 0;
      if (isInvoiceCovered(inv, paid) || invoiceStatus(inv) === "Paid") return;
      owed += invoiceBalance(inv, paid);
      count += 1;
    });
    return { owed, count };
  }, [invoices, paidByInvoiceId]);

  const hasFilters = filterProperty || searchText || filterStart || filterEnd;
  const clearFilters = () => {
    setFilterProperty("");
    setSearchText("");
    setFilterStart("");
    setFilterEnd("");
  };

  /* ------------------------------------------------------------------ */
  /* Bulk upload (deposits only; expense types are on the Expenses page) */
  /* ------------------------------------------------------------------ */

  const downloadTemplate = () => {
    const t = BULK_TEMPLATES.deposits;
    const ws = XLSX.utils.aoa_to_sheet([t.headers, Array(t.headers.length).fill("")]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, t.filename);
  };

  const parseBulkFile = async (file) => {
    if (!file) return;
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const raw = XLSX.utils.sheet_to_json(sheet);

    const parsed = raw
      .filter((r) => r.Property || r.Amount)
      .map((r, i) => {
        const date = normalizeSheetDate(r.Date);
        const rowData = {
          property: String(r.Property || "").trim(),
          amount: r.Amount ?? "",
          checkNumber: String(r["Check #"] ?? "").trim(),
          paymentType: r["Payment Type"] || "Deposit",
          invoiceNumber: String(r["Work Order #"] ?? "").trim(),
          invoiceNumber2: String(r["Invoice #2"] ?? "").trim(),
          description: r.Description || "",
          company: r.Company || "",
          date,
        };
        return { key: i, data: rowData, suggestedWO: suggestWOForRow(rowData.property, date) };
      });

    // Flag rows that match something already saved, or an earlier row in this file.
    const flagged = parsed.map((row, i) => {
      const existing = findMatchesForPriority(row.data, deposits);
      const earlier = findMatchesForPriority(
        row.data,
        parsed.slice(0, i).map((p) => ({ ...p.data, id: `file-${p.key}` }))
      );
      const dupNote = existing.length
        ? `Matches a payment already entered (${formatDateMDY(existing[0].date)}, ${depositSourceLabel(existing[0])})`
        : earlier.length
        ? "Appears twice in this file"
        : "";
      return { ...row, dupNote, include: !dupNote };
    });
    setBulkRows(flagged);
  };

  const updateBulkRow = (key, patch) =>
    setBulkRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch, data: { ...r.data, ...(patch.data || {}) } } : r)));

  const handleBulkSave = async () => {
    const rows = bulkRows.filter((r) => r.include);
    if (!rows.length) return;
    setIsBulkSaving(true);
    try {
      const added = [];
      const wos = new Set();
      for (const row of rows) {
        const payload = withJobLink({
          ...row.data,
          amount: parseAmount(row.data.amount),
          createdFrom: "DepositsBulkUpload",
          createdAt: new Date().toISOString(),
        });
        const ref = await addDoc(collection(db, "deposits"), payload);
        const txId = await upsertTransaction({
          txId: null,
          payload: buildTxFromDeposit({ id: ref.id, ...payload }, resolveJobIdFromWO(payload.invoiceNumber)),
        });
        await updateDoc(doc(db, "deposits", ref.id), { txId });
        added.push({ id: ref.id, ...payload, txId });
        if (payload.invoiceNumber) wos.add(payload.invoiceNumber);
      }
      setDeposits((prev) => sortByDateDesc([...prev, ...added]));
      for (const wo of wos) await resyncWO(wo);
      setBulkRows([]);
      setShowBulkUpload(false);
    } catch (err) {
      console.error(err);
      alert("Bulk save failed partway. Refresh to see what was saved.");
    } finally {
      setIsBulkSaving(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  const woOptionsFor = (property, current) => {
    const jobs = getWOsForProperty(property);
    const opts = jobs.map((job) => {
      const key = jobPaymentKey(job);
      return (
        <option key={key} value={key}>
          {job.wo ? job.wo : "No WO"} — {job.scope || job.serviceType || job.jobDescription || "No scope"}
          {isRecurring(job) ? " (recurring)" : ` (${formatShortDate(job.startDate)})`}
        </option>
      );
    });
    // Keep a WO that isn't in the list so opening Edit never silently clears it.
    if (current && !jobs.some((j) => jobPaymentKey(j) === String(current))) {
      opts.unshift(
        <option key={`cur-${current}`} value={current}>
          {current}
        </option>
      );
    }
    return opts;
  };

  const selectedJob = form.invoiceNumber ? jobsMap[String(form.invoiceNumber)] : null;
  const selectedJobStatus = form.invoiceNumber ? selectedJob?.status || "Not found" : "";

  // Invoices on the chosen WO, plus completed visits that have no invoice yet.
  const visitChoices = useMemo(() => {
    if (!formMode || !form.invoiceNumber) return { invoices: [], visits: [] };
    const key = String(form.invoiceNumber);
    const woInvoices = invoices
      .filter((inv) => invoicePaymentKey(inv) === key && !isVoidInvoice(inv))
      .sort((a, b) => invoiceVisitKey(b).localeCompare(invoiceVisitKey(a)));
    const job = jobsMap[key];
    const visits = [];
    if (job && isRecurring(job)) {
      const invoiced = new Set(woInvoices.map((inv) => canonicalVisitForInvoice(inv)).filter(Boolean));
      getCompletedDateSet(job).forEach((k) => {
        const canon = getCanonicalKeyForScheduledKey(job, k) || k;
        if (!invoiced.has(canon)) visits.push(canon);
      });
      visits.sort((a, b) => b.localeCompare(a));
    }
    return { invoices: woInvoices, visits: Array.from(new Set(visits)) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMode, form.invoiceNumber, invoices, jobsMap]);

  const visitValue = form.approvedInvoiceId ? `inv:${form.approvedInvoiceId}` : form.occurrenceKey ? `visit:${form.occurrenceKey}` : "";

  // Open invoices at the chosen property (for the "Pay an open invoice" shortcut).
  const openInvoicesForProperty = useMemo(() => {
    if (!formMode || formMode !== "add" || !form.property) return [];
    return invoices
      .filter((inv) => invoiceSite(inv) === form.property && !isVoidInvoice(inv) && invoiceTotal(inv) > 0)
      .filter((inv) => !isInvoiceCovered(inv, paidByInvoiceId[inv.id] || 0) && invoiceStatus(inv) !== "Paid")
      .sort((a, b) => invoiceVisitKey(a).localeCompare(invoiceVisitKey(b)));
  }, [formMode, form.property, invoices, paidByInvoiceId]);

  const linkedInvoiceFor = (entry) => (entry.approvedInvoiceId ? invoices.find((i) => i.id === entry.approvedInvoiceId) : null);

  return (
    <div className="fin-page">
      <header className="fin-header">
        <div>
          <h1 className="fin-title">Deposits</h1>
          <p className="fin-subtitle">
            Customer payments received by Priority Pest Defense.{" "}
            <button className="fin-linkbtn" onClick={() => setShowHelp((v) => !v)}>
              {showHelp ? "Hide details" : "How this page works"}
            </button>
          </p>
        </div>
        <div className="fin-header__actions">
          <div className="fin-menu">
            <button className="fin-btn fin-btn--secondary" onClick={() => setShowTools((v) => !v)}>
              Tools ▾
            </button>
            {showTools && (
              <div className="fin-menu__panel" onMouseLeave={() => setShowTools(false)}>
                <button onClick={backfillJobTotalsFromExistingChecks} disabled={isBackfilling}>
                  <strong>Recalculate job payment totals</strong>
                  <span>{backfillStatus || "Rebuilds paid totals and invoice Paid status for every work order"}</span>
                </button>
                <button onClick={backfillTransactions} disabled={isTxBackfilling}>
                  <strong>Send missing entries to Financials</strong>
                  <span>{txBackfillStatus || "Adds any deposit or expense not yet in the Financials ledger"}</span>
                </button>
              </div>
            )}
          </div>
          <button className="fin-btn fin-btn--secondary" onClick={() => setShowBulkUpload(true)}>
            Bulk Upload
          </button>
          <button className="fin-btn fin-btn--primary" onClick={openAdd}>
            + Add Payment
          </button>
        </div>
      </header>

      {showHelp && (
        <div className="fin-note">
          <p>
            Payments entered here, with the <b>Paid</b> button on <b>Approved Invoices</b>, on <b>Job Estimate Spreadsheet</b>,
            and on <b>Bank Statement</b> (money in tied to a work order) all save to the same list, so they all show below. Each
            payment updates the job's paid total, marks the invoice Paid once it's covered, and is sent to the Financials
            Dashboard.
          </p>
          <p>
            Recurring service: every visit of a recurring job shares one work order. Pick the invoice or visit a check pays
            under <b>Pays invoice / visit</b>. If you leave it blank, the money goes to that work order's oldest unpaid visit
            first.
          </p>
          <p>
            Because payments can come in from several places, the same check can end up here twice. Possible duplicates are
            grouped together and flagged — use <b>Review duplicates</b> to pick one to keep.
          </p>
          <p>Subcontractor pay, employee pay, monthly bills and bank deductions are on the <b>Expenses</b> page.</p>
        </div>
      )}

      {duplicateGroups.length > 0 && (
        <div className="fin-alert">
          <div className="fin-alert__icon" aria-hidden>!</div>
          <div className="fin-alert__body">
            <strong>
              {duplicateGroups.length} possible duplicate payment{duplicateGroups.length === 1 ? "" : "s"} found
            </strong>
            <span>
              Up to {formatMoney(duplicateExposure)} may be counted twice. Review them and delete the extra copies.
            </span>
          </div>
          <button className="fin-btn fin-btn--warn" onClick={() => openReview()}>
            Review duplicates
          </button>
        </div>
      )}

      <section className="fin-stats">
        <div className="fin-stat">
          <span className="fin-stat__label">{hasFilters ? "Filtered total" : "Total received"}</span>
          <span className="fin-stat__value">{formatMoney(viewTotal)}</span>
          <span className="fin-stat__sub">
            {viewCount} payment{viewCount === 1 ? "" : "s"} · {viewRange}
          </span>
        </div>
        <div className="fin-stat">
          <span className="fin-stat__label">All time</span>
          <span className="fin-stat__value">{formatMoney(allTimeTotal)}</span>
          <span className="fin-stat__sub">
            {deposits.length} entries · {formatMoney(openInvoiceSummary.owed)} still owed on {openInvoiceSummary.count} invoice
            {openInvoiceSummary.count === 1 ? "" : "s"}
          </span>
        </div>
        <div className="fin-stat fin-stat--wide">
          <span className="fin-stat__label">By company{hasFilters ? " (filtered)" : ""}</span>
          <div className="fin-breakdown">
            {companyTotals.length === 0 && <span className="fin-muted">—</span>}
            {companyTotals.slice(0, 4).map(([name, total]) => (
              <div key={name} className="fin-breakdown__row">
                <span>{name}</span>
                <b>{formatMoney(total)}</b>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="fin-card">
        <div className="fin-toolbar">
          <input
            className="fin-input fin-input--search"
            type="search"
            placeholder="Search property, company, check #, WO…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <select className="fin-input" value={filterProperty} onChange={(e) => setFilterProperty(e.target.value)}>
            <option value="">All properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.propertyName}>
                {p.propertyName}
              </option>
            ))}
          </select>
          <label className="fin-inline">
            <span>From</span>
            <input className="fin-input" type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} />
          </label>
          <label className="fin-inline">
            <span>To</span>
            <input className="fin-input" type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} />
          </label>
          <label className="fin-inline">
            <span>Sort</span>
            <select className="fin-input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="date">Newest first</option>
              <option value="amount">Amount</option>
              <option value="wo">Work order #</option>
              <option value="property">Property</option>
              <option value="company">Company</option>
            </select>
          </label>
          {hasFilters && (
            <button className="fin-linkbtn" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>

        <div className="fin-table-wrap">
          <table className="fin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Property</th>
                <th>Company</th>
                <th className="fin-num">Amount</th>
                <th>Check #</th>
                <th>Type</th>
                <th>Work Order</th>
                <th>Invoice #2</th>
                <th>Description</th>
                <th>Source</th>
                <th className="fin-right"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="11" className="fin-empty">Loading…</td>
                </tr>
              )}
              {!loading && displayRows.length === 0 && (
                <tr>
                  <td colSpan="11" className="fin-empty">No payments match the current filters.</td>
                </tr>
              )}
              {displayRows.map((entry) => {
                const dupGroup = !entry.__isSplitSummary ? dupGroupById[entry.id] : null;
                const cls = [
                  dupGroup ? "is-dup" : "",
                  entry.__dupFirst ? "is-dup-first" : "",
                  entry.__dupLast ? "is-dup-last" : "",
                  entry.__isSplitSummary ? "is-split" : "",
                  entry.__isSplitChild ? "is-split-child" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                const inv2 = entry.invoiceNumber2 || entry.__linkedWOList?.join(", ") || "";
                const linkedInv = !entry.__isSplitSummary ? linkedInvoiceFor(entry) : null;
                return (
                  <tr key={entry.id} className={cls}>
                    <td className="fin-nowrap">{formatDateMDY(entry.date)}</td>
                    <td className="fin-strong">{entry.__isSplitChild ? "↳ " : ""}{entry.property}</td>
                    <td className="fin-truncate fin-truncate--sm" title={entry.company}>{entry.company}</td>
                    <td className="fin-num fin-strong">{formatMoney(parseAmount(entry.__displayAmount ?? entry.amount))}</td>
                    <td>{entry.checkNumber}</td>
                    <td>{entry.paymentType || "Other"}</td>
                    <td>
                      {entry.invoiceNumber}
                      {!entry.__isSplitSummary && (entry.occurrenceKey || linkedInv) && (
                        <div className="fin-cell-note">
                          {entry.occurrenceKey ? `Visit ${formatDateMDY(entry.occurrenceKey)}` : ""}
                          {linkedInv ? `${entry.occurrenceKey ? " · " : ""}Invoice ${invoiceStatus(linkedInv)}` : ""}
                        </div>
                      )}
                      {entry.__isSplitSummary && (
                        <div className="fin-cell-note">
                          Split check · {entry.__splitCount || 0} WO{entry.__splitCount === 1 ? "" : "s"}
                          {entry.__invoiceOnlyWOList?.length > 0 && <> · main {entry.__invoiceOnlyWOList.join(", ")}</>}
                        </div>
                      )}
                    </td>
                    <td className="fin-truncate" title={inv2}>{inv2}</td>
                    <td className="fin-truncate" title={entry.description}>{entry.description}</td>
                    <td>
                      <span className={`fin-tag fin-tag--${sourceTagClass(entry)}`}>{depositSourceLabel(entry)}</span>
                      {dupGroup && (
                        <button className="fin-dup-badge" onClick={() => openReview(dupGroup.key)}>
                          Possible duplicate
                        </button>
                      )}
                      {entry.__hasDuplicateChild && <span className="fin-dup-badge fin-dup-badge--static">Has duplicate</span>}
                    </td>
                    <td className="fin-right fin-nowrap">
                      {entry.__isSplitSummary ? (
                        <button className="fin-btn fin-btn--ghost fin-btn--sm" onClick={() => setExpandedSplitGroups((p) => ({ ...p, [entry.__splitGroupKey]: !p[entry.__splitGroupKey] }))}>
                          {expandedSplitGroups[entry.__splitGroupKey] ? "Hide split" : "Show split"}
                        </button>
                      ) : (
                        <>
                          <button className="fin-btn fin-btn--ghost fin-btn--sm" onClick={() => openEdit(entry)}>Edit</button>
                          <button className="fin-btn fin-btn--ghost-danger fin-btn--sm" onClick={() => handleDelete(entry)}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ------------------------- Add / edit payment ------------------------ */}
      {formMode && (
        <DepositsFinModal onClose={closeForm} size="md">
          <div className="fin-modal__head">
            <h2>{formMode === "add" ? "Add Payment" : "Edit Payment"}</h2>
            <button className="fin-icon-btn" onClick={closeForm} aria-label="Close">×</button>
          </div>
          <div className="fin-modal__body">
            {formMode === "edit" && isBankStatementDeposit(form) && (
              <div className="fin-note fin-note--sm">
                This payment came from Bank Statement. Its amount in Financials comes from the bank statement line, so change
                the amount there if it's wrong.
              </div>
            )}
            <div className="fin-form">
              <label className="fin-field fin-field--full">
                <span>Property <em>*</em></span>
                <select name="property" value={form.property} onChange={handleFormChange}>
                  <option value="">Select property</option>
                  {form.property && !properties.some((p) => p.propertyName === form.property) && (
                    <option value={form.property}>{form.property}</option>
                  )}
                  {properties.map((p) => (
                    <option key={p.id} value={p.propertyName}>{p.propertyName}</option>
                  ))}
                </select>
              </label>
              {formMode === "add" && openInvoicesForProperty.length > 0 && (
                <label className="fin-field fin-field--full">
                  <span>Pay an open invoice (optional)</span>
                  <select
                    value={form.approvedInvoiceId || ""}
                    onChange={(e) => {
                      const inv = invoices.find((i) => i.id === e.target.value);
                      if (inv) applyInvoiceToForm(inv);
                      else setForm((prev) => ({ ...prev, approvedInvoiceId: "", occurrenceKey: "" }));
                    }}
                  >
                    <option value="">— Pick an invoice to fill in WO, visit and amount —</option>
                    {openInvoicesForProperty.map((inv) => (
                      <option key={inv.id} value={inv.id}>{invoiceLabel(inv)}</option>
                    ))}
                  </select>
                </label>
              )}
              <label className="fin-field">
                <span>Amount <em>*</em></span>
                <input name="amount" type="number" step="0.01" inputMode="decimal" value={form.amount} onChange={handleFormChange} />
              </label>
              <label className="fin-field">
                <span>Date <em>*</em></span>
                <input name="date" type="date" value={form.date} onChange={handleFormChange} />
              </label>
              <label className="fin-field">
                <span>Check #</span>
                <input name="checkNumber" value={form.checkNumber || ""} onChange={handleFormChange} />
              </label>
              <label className="fin-field">
                <span>Payment Type</span>
                <select name="paymentType" value={form.paymentType || "Other"} onChange={handleFormChange}>
                  {paymentTypeOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
              <label className="fin-field fin-field--full">
                <span>Work Order #</span>
                <select name="invoiceNumber" value={form.invoiceNumber || ""} onChange={handleFormChange} disabled={!form.property}>
                  <option value="">{form.property ? "Select work order" : "Select a property first"}</option>
                  {woOptionsFor(form.property, form.invoiceNumber)}
                </select>
                {selectedJobStatus && <small className="fin-muted">Job status: <b>{selectedJobStatus}</b></small>}
              </label>
              {form.invoiceNumber && (visitChoices.invoices.length > 0 || visitChoices.visits.length > 0 || form.occurrenceKey) && (
                <label className="fin-field fin-field--full">
                  <span>Pays invoice / visit</span>
                  <select value={visitValue} onChange={(e) => handleVisitChoice(e.target.value)}>
                    <option value="">Not sure — apply to the oldest unpaid visit</option>
                    {visitChoices.invoices.map((inv) => (
                      <option key={inv.id} value={`inv:${inv.id}`}>{invoiceLabel(inv)}</option>
                    ))}
                    {visitChoices.visits.map((k) => (
                      <option key={k} value={`visit:${k}`}>Visit {formatDateMDY(k)} · completed, no invoice yet</option>
                    ))}
                    {form.occurrenceKey && !form.approvedInvoiceId && !visitChoices.visits.includes(form.occurrenceKey) && (
                      <option value={`visit:${form.occurrenceKey}`}>Visit {formatDateMDY(form.occurrenceKey)}</option>
                    )}
                  </select>
                  {selectedJob && isRecurring(selectedJob) && (
                    <small className="fin-muted">Recurring job — every visit shares this work order.</small>
                  )}
                </label>
              )}
              <label className="fin-field">
                <span>Invoice #2</span>
                <input name="invoiceNumber2" placeholder="Optional" value={form.invoiceNumber2 || ""} onChange={handleFormChange} />
              </label>
              <label className="fin-field">
                <span>Company</span>
                <select name="company" value={form.company || ""} onChange={handleFormChange}>
                  <option value="">Select company</option>
                  {form.company && !companies.some((c) => c.companyName === form.company) && (
                    <option value={form.company}>{form.company}</option>
                  )}
                  {companies.map((c) => (
                    <option key={c.id} value={c.companyName}>{c.companyName}</option>
                  ))}
                </select>
              </label>
              <label className="fin-field fin-field--full">
                <span>Description</span>
                <input name="description" value={form.description || ""} onChange={handleFormChange} />
              </label>
            </div>
          </div>
          <div className="fin-modal__foot">
            <button className="fin-btn fin-btn--secondary" onClick={closeForm}>Cancel</button>
            <button className="fin-btn fin-btn--primary" onClick={handleSubmitForm} disabled={isSaving}>
              {isSaving ? "Saving…" : formMode === "add" ? "Save Payment" : "Save Changes"}
            </button>
          </div>
        </DepositsFinModal>
      )}

      {/* ----------------------- Duplicate warning on add -------------------- */}
      {dupConfirm && (
        <DepositsFinModal onClose={() => setDupConfirm(null)} size="md" level={1} role="alertdialog">
          <div className="fin-modal__head">
            <h2>This payment may already be entered</h2>
          </div>
          <div className="fin-modal__body">
            <p className="fin-muted">
              You're adding {formatMoney(dupConfirm.data.amount)} for <b>{dupConfirm.data.property}</b>
              {dupConfirm.data.checkNumber ? <> (check {dupConfirm.data.checkNumber})</> : null}. It matches:
            </p>
            <DupList items={dupConfirm.matches} />
          </div>
          <div className="fin-modal__foot">
            <button className="fin-btn fin-btn--secondary" onClick={() => setDupConfirm(null)}>Don't save</button>
            <button
              className="fin-btn fin-btn--primary"
              disabled={isSaving}
              onClick={async () => {
                const data = dupConfirm.data;
                setDupConfirm(null);
                await saveNewCheck(data);
              }}
            >
              It's a different payment — save
            </button>
          </div>
        </DepositsFinModal>
      )}

      {/* ---------------------------- Review dups ---------------------------- */}
      {showReview && (
        <DepositsFinModal onClose={() => setShowReview(false)} size="lg">
          <div className="fin-modal__head">
            <div>
              <h2>Review Possible Duplicates</h2>
              <p className="fin-modal__sub">
                Tick Keep on the real payment in each group (the recommended one is pre-selected). Entries marked Delete are
                removed along with their Financials entry, and the job's paid total and invoice status are recalculated.
              </p>
            </div>
            <button className="fin-icon-btn" onClick={() => setShowReview(false)} aria-label="Close">×</button>
          </div>
          <div className="fin-modal__body">
            {duplicateGroups.length === 0 && <p className="fin-empty">No duplicates left.</p>}
            {duplicateGroups.map((group) => {
              const removing = group.items.filter((d) => !isKept(group, d.id));
              const keptAny = group.items.filter((d) => isKept(group, d.id));
              const deletingBank = removing.some(isBankStatementDeposit) && !keptAny.some(isBankStatementDeposit);
              const deletingSplit = removing.some((d) => d.splitCheck);
              const busy = reviewBusy === group.key;
              return (
                <section key={group.key} id={`dup-${group.key}`} className={`fin-dupgroup ${reviewFocus === group.key ? "is-focus" : ""}`}>
                  <div className="fin-dupgroup__head">
                    <div>
                      <strong>{group.items[0].property}</strong> · {formatMoney(parseAmount(group.items[0].amount))} ·{" "}
                      {group.items.length} entries
                      <div className="fin-muted fin-small">{group.reason}</div>
                    </div>
                  </div>
                  <div className="fin-table-wrap">
                    <table className="fin-table fin-table--compact">
                      <thead>
                        <tr>
                          <th>Keep</th>
                          <th>Date</th>
                          <th>Check #</th>
                          <th>WO</th>
                          <th>Visit</th>
                          <th>Company</th>
                          <th>Description</th>
                          <th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((d) => (
                          <tr key={d.id} className={isKept(group, d.id) ? "is-keep" : "is-remove"}>
                            <td>
                              <label className="fin-radio">
                                <input type="checkbox" checked={isKept(group, d.id)} onChange={() => toggleKeep(group, d.id)} />
                                <span>{isKept(group, d.id) ? "Keep" : "Delete"}</span>
                              </label>
                              {d.id === group.keepId && <div className="fin-small fin-muted">Recommended</div>}
                            </td>
                            <td className="fin-nowrap">{formatDateMDY(d.date)}</td>
                            <td>{d.checkNumber || "—"}</td>
                            <td>{d.invoiceNumber || "—"}</td>
                            <td className="fin-nowrap">{d.occurrenceKey ? formatDateMDY(d.occurrenceKey) : "—"}</td>
                            <td>{d.company || "—"}</td>
                            <td className="fin-truncate" title={d.description}>{d.description || "—"}</td>
                            <td>{depositSourceLabel(d)}{d.splitCheck ? " · split" : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {deletingBank && (
                    <p className="fin-warn-text">
                      Heads up: the Bank Statement copy's money is still counted in Financials through Bank Statement Entries.
                      Keeping the other copy will count this payment twice there. Keeping the Bank Statement copy is safer.
                    </p>
                  )}
                  {deletingSplit && (
                    <p className="fin-warn-text">
                      One of the entries you're deleting is part of a split check. Deleting it removes that piece of the split.
                    </p>
                  )}
                  <div className="fin-dupgroup__actions">
                    <button className="fin-btn fin-btn--secondary fin-btn--sm" disabled={busy} onClick={() => dismissGroup(group)}>
                      Not a duplicate
                    </button>
                    <button className="fin-btn fin-btn--danger fin-btn--sm" disabled={busy} onClick={() => deleteDuplicatesInGroup(group)}>
                      {busy ? "Working…" : `Delete ${removing.length} duplicate${removing.length === 1 ? "" : "s"}`}
                    </button>
                  </div>
                </section>
              );
            })}
          </div>
          <div className="fin-modal__foot">
            <button className="fin-btn fin-btn--primary" onClick={() => setShowReview(false)}>Done</button>
          </div>
        </DepositsFinModal>
      )}

      {/* ---------------------------- Bulk upload ---------------------------- */}
      {showBulkUpload && (
        <DepositsFinModal
          onClose={() => {
            if (isBulkSaving) return;
            setShowBulkUpload(false);
            setBulkRows([]);
          }}
          size="lg"
        >
          <div className="fin-modal__head">
            <div>
              <h2>Bulk Upload Payments</h2>
              <p className="fin-modal__sub">
                Columns: {BULK_TEMPLATES.deposits.headers.join(" · ")}. Expense uploads are on the Expenses page.
              </p>
            </div>
            <button className="fin-icon-btn" onClick={() => { setShowBulkUpload(false); setBulkRows([]); }} aria-label="Close">×</button>
          </div>
          <div className="fin-modal__body">
            <div className="fin-upload-row">
              <button className="fin-btn fin-btn--secondary fin-btn--sm" onClick={downloadTemplate}>Download template</button>
              <input type="file" accept=".csv,.xlsx" onChange={(e) => parseBulkFile(e.target.files[0])} />
            </div>

            {bulkRows.length > 0 && (
              <>
                {bulkRows.some((r) => r.dupNote) && (
                  <p className="fin-warn-text">
                    Rows that look like payments already entered are unchecked. Tick them only if they're really new.
                  </p>
                )}
                <div className="fin-table-wrap">
                  <table className="fin-table fin-table--compact">
                    <thead>
                      <tr>
                        <th>Save</th>
                        <th>Date</th>
                        <th>Property</th>
                        <th className="fin-num">Amount</th>
                        <th>Check #</th>
                        <th>Work Order</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkRows.map((r) => (
                        <tr key={r.key} className={r.dupNote ? "is-dup" : ""}>
                          <td>
                            <input type="checkbox" checked={r.include} onChange={(e) => updateBulkRow(r.key, { include: e.target.checked })} />
                          </td>
                          <td className="fin-nowrap">{formatDateMDY(r.data.date)}</td>
                          <td>{r.data.property}</td>
                          <td className="fin-num">{formatMoney(parseAmount(r.data.amount))}</td>
                          <td>{r.data.checkNumber}</td>
                          <td>
                            <select
                              className="fin-input fin-input--sm"
                              value={r.data.invoiceNumber}
                              onChange={(e) => updateBulkRow(r.key, { data: { invoiceNumber: e.target.value } })}
                            >
                              <option value="">—</option>
                              {woOptionsFor(r.data.property, r.data.invoiceNumber)}
                            </select>
                            {r.suggestedWO && r.suggestedWO !== r.data.invoiceNumber && (
                              <button className="fin-linkbtn fin-small" onClick={() => updateBulkRow(r.key, { data: { invoiceNumber: r.suggestedWO } })}>
                                Use suggested {r.suggestedWO}
                              </button>
                            )}
                          </td>
                          <td className="fin-small">{r.dupNote && <span className="fin-dup-badge fin-dup-badge--static">{r.dupNote}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <div className="fin-modal__foot">
            <button className="fin-btn fin-btn--secondary" onClick={() => { setShowBulkUpload(false); setBulkRows([]); }} disabled={isBulkSaving}>
              Cancel
            </button>
            <button
              className="fin-btn fin-btn--primary"
              onClick={handleBulkSave}
              disabled={isBulkSaving || !bulkRows.some((r) => r.include)}
            >
              {isBulkSaving ? "Saving…" : `Save ${bulkRows.filter((r) => r.include).length} payment(s)`}
            </button>
          </div>
        </DepositsFinModal>
      )}
    </div>
  );
};

const DupList = ({ items }) => (
  <ul className="fin-duplist">
    {items.map((d) => (
      <li key={d.id}>
        <b>{formatDateMDY(d.date)}</b> · {formatMoney(parseAmount(d.amount))} · check {d.checkNumber || "—"} · WO{" "}
        {d.invoiceNumber || "—"}
        {d.occurrenceKey ? <> · visit {formatDateMDY(d.occurrenceKey)}</> : null}{" "}
        <span className="fin-muted">({depositSourceLabel(d)})</span>
      </li>
    ))}
  </ul>
);

export default Deposits;
